<template>
  <div
    v-if="product"
    ref="stickyAddToCart"
    class="sticky-add-to-cart"
    :class="disabledClass"
  >
    <div class="sticky-add-to-cart__container">
      <div class="sticky-add-to-cart__left">
        <span
          v-if="product.vendor || liquid.vendor"
          class="sticky-add-to-cart__vendor text-heading-m"
          v-text="product.vendor || liquid.vendor"
        />

        <span
          class="sticky-add-to-cart__title text-body-m"
          v-text="product.title || liquid.title"
        />
      </div>

      <div class="sticky-add-to-cart__right">
        <product-prices
          :available="product?.available"
          block-class="sticky-add-to-cart"
          class="limoniapps-discountninja-productprice"
          :compare-at-price="variant?.compareAtPrice?.amount"
          :price="variant?.price?.amount || liquid.price"
          component-location="stickyATC"
        />

        <quantity-selector
          block-class="sticky-add-to-cart"
          large
          :item="variant"
          :quantity="quantity"
          :state="state"
          @update-quantity="handleQuantityUpdate"
        />

        <btn
          :class="disabledClass"
          class="sticky-add-to-cart__add-to-cart"
          block-class="sticky-add-to-cart"
          :label="addToCartText"
          modifiers="large block"
          :show-state-icon="state === 'adding'"
          :show-icon="false"
          type="submit"
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>

        <animated-wishlist-button
          :product-id="product.id"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Sticky add to cart (sticky-add-to-cart)
 * -----------------------------------------------------------------------------
 * Sticky Add to Cart button for the PDP.
 *
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Number} quantity - Current product quantity.
 * @param {String} state - Current product state.
 * @param {String} error - Error message.
 *
 */
import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import AnimatedWishlistButton from '~async/animated-wishlist-button/animated-wishlist-button'

import IconLoading from '~icons/misc/loading.svg'

import { mapState, mapActions } from 'vuex'
import { scrollToElement } from '~/helpers/general'

export default {
  name: 'StickyAddToCart',

  components: {
    ProductPrices,
    QuantitySelector,
    AnimatedWishlistButton,
    IconLoading,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        hasOnlyDefaultVariant: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    quantity: {
      type: Number,
      default: 1,
    },
    state: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
  },

  emits: ['quantity-change', 'add-to-cart'],

  data() {
    return {
      isAddingToCart: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      scrollPosition: (state) => state.index.scroll.y,
    }),

    /**
     * Handle disabled class.
     * @returns {Object}
     */
    disabledClass() {
      return {
        [this.$class('disabled')]: this.state === 'out of stock',
      }
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.state === 'out of stock') {
        return this.$string('product.cta.sold_out')
      }

      if (this.state === 'added') {
        return this.$string('product.cta.added_to_cart')
      }

      return this.$string('product.cta.add_to_cart')
    },
  },

  watch: {

    /**
     * Watch the scroll position to determine when the
     * sticky add to cart should be shown
     */
    scrollPosition() {
      if (this.isMobile) {
        return
      }

      const productFormCta = document.querySelector('[js-product-form="addToCart"]')
      const ctaPosition = productFormCta?.offsetTop
      const stickyAddToCart = this.$refs.stickyAddToCart

      if (!stickyAddToCart) {
        return
      }

      if (this.scrollPosition > ctaPosition) {
        stickyAddToCart.classList.add(this.$class('active'))
      }

      if (this.scrollPosition <= ctaPosition) {
        stickyAddToCart.classList.remove(this.$class('active'))
      }
    },

    /**
     * Watch error state to scroll back up if there is an error message.
     */
    error() {
      if (this.error === '') {
        return
      }

      const productForm = document.querySelector('[js-product-form="form"]')
      const productTitle = document.querySelector('[js-main-product="title"]')

      if (!productForm || !productTitle) {
        return
      }

      if (this.isMobile) {
        scrollToElement(productForm)
      } else {
        scrollToElement(productTitle)
      }

    },
  },

  mounted() {
    document.body.classList.add('sticky-add-to-cart-ready')
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle change event on quantity change.
     * @param {Number} quantity - Quantity.
     */
    handleQuantityUpdate(quantity) {
      this.$emit('quantity-change', quantity)
    },

    /**
     * Handle change event on quantity change.
     * @param {Number} quantity - Quantity.
     */
    handleAddToCart() {
      this.$emit('add-to-cart')
    },

  },
}
</script>

<style lang="scss">
@import './sticky-add-to-cart';
</style>
